import type { EnemyFamilyId, EnemyIdleMotion, MotionPrefs } from './types'

const IDLE: Record<EnemyFamilyId, EnemyIdleMotion> = {
  normal: { pulseAmplitude: 0.03, pulseHz: 1.2, forwardLean: 0 },
  fast: { pulseAmplitude: 0.045, pulseHz: 2.4, forwardLean: 0.08 },
  tank: { pulseAmplitude: 0.015, pulseHz: 0.55, forwardLean: 0 },
  regen: { pulseAmplitude: 0.04, pulseHz: 1.0, forwardLean: 0 },
  shield: { pulseAmplitude: 0.025, pulseHz: 0.85, forwardLean: 0 },
  boss: { pulseAmplitude: 0.05, pulseHz: 0.7, forwardLean: 0 },
  flyer: { pulseAmplitude: 0.055, pulseHz: 2.8, forwardLean: 0.04 },
  air_boss: { pulseAmplitude: 0.06, pulseHz: 1.1, forwardLean: 0 },
  guardian: { pulseAmplitude: 0.018, pulseHz: 0.6, forwardLean: 0 },
  phase: { pulseAmplitude: 0.05, pulseHz: 2.0, forwardLean: 0.05 },
  crystal: { pulseAmplitude: 0.035, pulseHz: 1.4, forwardLean: 0 }
}

export function enemyFamilyId(definitionId: string): EnemyFamilyId {
  if (
    definitionId === 'fast' ||
    definitionId === 'tank' ||
    definitionId === 'regen' ||
    definitionId === 'shield' ||
    definitionId === 'boss' ||
    definitionId === 'flyer' ||
    definitionId === 'air_boss' ||
    definitionId === 'guardian' ||
    definitionId === 'phase' ||
    definitionId === 'crystal'
  ) {
    return definitionId
  }
  return 'normal'
}

/** True for AIR combat families (presentation altitude / shadow). */
export function isAirEnemyFamily(definitionId: string): boolean {
  const family = enemyFamilyId(definitionId)
  return family === 'flyer' || family === 'air_boss'
}

export function enemyIdleMotion(definitionId: string, prefs: MotionPrefs): EnemyIdleMotion {
  const base = IDLE[enemyFamilyId(definitionId)]
  if (prefs.reducedMotion) {
    return { pulseAmplitude: 0, pulseHz: 0, forwardLean: 0 }
  }
  return { ...base }
}

export function enemyIdlePhase(enemyId: string): number {
  let h = 0
  for (let i = 0; i < enemyId.length; i += 1) h = (h * 17 + enemyId.charCodeAt(i)) >>> 0
  return (h % 628) / 100
}

export function sampleEnemyIdle(
  motion: EnemyIdleMotion,
  simTimeSec: number,
  phase: number,
  moving: boolean
): { scaleX: number; scaleY: number } {
  const pulse =
    motion.pulseAmplitude <= 0
      ? 0
      : Math.sin(simTimeSec * motion.pulseHz * Math.PI * 2 + phase) * motion.pulseAmplitude
  const lean = moving ? motion.forwardLean : 0
  return {
    scaleX: 1 + pulse + lean,
    scaleY: 1 + pulse - lean * 0.5
  }
}

/** Boss outer ring counter-rotation rate (rad/sim-sec). */
export function bossRingSpin(prefs: MotionPrefs): number {
  return prefs.reducedMotion ? 0 : 0.7
}

export function enemySpawnStartScale(prefs: MotionPrefs, heavy = false): number {
  if (prefs.reducedMotion) return heavy ? 0.78 : 0.85
  return heavy ? 0.08 : 0.2
}

export function enemySpawnSimMs(prefs: MotionPrefs, heavy = false): number {
  if (prefs.reducedMotion) return heavy ? 90 : 60
  return heavy ? 320 : 180
}

export function enemyDeathBurstScale(prefs: MotionPrefs, heavy = false): number {
  if (prefs.reducedMotion) return heavy ? 1.25 : 1.15
  return heavy ? 2.05 : 1.65
}

export function enemyDeathSimMs(prefs: MotionPrefs, heavy = false): number {
  if (prefs.reducedMotion) return heavy ? 120 : 90
  return heavy ? 360 : 240
}
